<template>
    <v-form ref="form" v-model="valid" class="pb-4">
        <v-row dense class="mb-4">
            <v-col cols="12" sm="6" md="2" />
            <v-col cols="12" sm="6" md="6" class="align-center">
                <div class="width-max-content ml-auto">
                    <v-switch
                        v-model="formData.active"
                        color="primary"
                        class="ml-auto mr-4 mb-4"
                        :label="formData.active ? $t('active') : $t('inactive')"
                        data-cy="status"
                        hide-details
                        density="compact"
                    />
                </div>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="4">
                <v-select
                    v-model="formData.categoryId"
                    :items="catalogue"
                    item-value="id"
                    item-title="name"
                    :label="`${$t('transport')}*`"
                    density="compact"
                    variant="outlined"
                    color="primary"
                    :disabled="!!ediServiceId"
                    data-cy="category-id"
                    :rules="[requiredRule('transport')]"
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" sm="6" md="4">
                <div class="text-subtitle-1">{{ $t('departure_time_local') }}*:</div>
                <v-radio-group v-model="formData.ownTransferServiceTime" density="compact">
                    <v-radio
                        v-for="{value, title} in useTimeInterval"
                        :key="value"
                        :label="title"
                        :value="value"
                        color="primary"
                        :data-cy="value"
                    />
                </v-radio-group>
            </v-col>
        </v-row>
        <v-row v-if="formData.ownTransferServiceTime !== TimeInterval.AnyTime" dense>
            <v-col cols="6" sm="3" md="2">
                <v-text-field
                    v-model="formData.startTime"
                    :label="`${$t('start_time')} (HH:mm)*`"
                    :rules="[requiredRule('start_time'), timeFormatRule()]"
                    variant="outlined"
                    color="primary"
                    density="compact"
                    data-cy="start-time"
                    maxlength="5"
                    @update:model-value="e => formatTime(e, 'startTime')"
                />
            </v-col>
            <v-col cols="6" sm="3" md="2">
                <v-text-field
                    v-if="formData.ownTransferServiceTime === TimeInterval.Interval"
                    v-model="formData.startTimeTo"
                    :label="`${$t('start_time_to')} (HH:mm)`"
                    :rules="[timeFormatRule()]"
                    variant="outlined"
                    color="primary"
                    density="compact"
                    data-cy="start-time"
                    maxlength="5"
                    @update:model-value="e => formatTime(e, 'startTimeTo')"
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="10" sm="6" md="4">
                <div class="text-subtitle-1 mt-3">{{ $t('driver_language') }}:</div>
            </v-col>
            <v-col cols="2">
                <v-btn size="small" icon="mdi-plus" variant="text" color="primary" data-cy="add-row" @click="addRow" />
            </v-col>
        </v-row>
        <div class="pb-8">
            <div v-if="!useLanguageCodesUnique" class="text-subtitle-2 pt-1 text-red-darken-4 font-weight-regular">
                {{ `${$t('driver_language')} ${$t('validation.unique')}` }}
            </div>
            <v-row v-for="service in formData.ownTransferServiceDriverDescriptions" :key="service.id" dense>
                <v-col cols="10" sm="6" md="4">
                    <v-autocomplete
                        v-model="service.languageCode"
                        :items="useLanguages"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="language"
                        hide-details
                    />
                </v-col>
                <v-col cols="2">
                    <v-btn
                        size="small"
                        icon="mdi-close"
                        variant="text"
                        color="error"
                        class="ml-auto"
                        :data-cy="`remove-${service.id}`"
                        @click="removeRow(service.id)"
                    />
                </v-col>
            </v-row>
        </div>
        <div class="text-subtitle-1">{{ $t('min_max_occupancy') }}:</div>
        <v-row dense>
            <v-col class="flex-grow-0">
                <v-text-field
                    v-model="formData.minOccupancy"
                    type="number"
                    hide-spin-buttons
                    variant="outlined"
                    color="primary"
                    density="compact"
                    data-cy="min-occupancy"
                    width="100px"
                />
            </v-col>
            <v-col class="flex-grow-0">
                <v-text-field
                    v-model="formData.maxOccupancy"
                    type="number"
                    hide-spin-buttons
                    variant="outlined"
                    color="primary"
                    density="compact"
                    data-cy="max-occupancy"
                    width="100px"
                />
            </v-col>
        </v-row>
        <v-row no-gutters class="gc-4">
            <v-btn data-cy="save" :disabled="isLoading || !valid || !useLanguageCodesUnique" @click="save()">
                {{ $t('save') }}
            </v-btn>
            <v-btn data-cy="cancel" :disabled="isLoading" variant="text" @click="emit('cancel')">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import {initTransferServiceFormData} from '~/utils/types/account/products/helpers'
    import {requiredRule} from '~/composables/rules'
    import {nanoid} from 'nanoid'
    import {useTimeInterval} from '~/composables/form-helpers'
    import {TimeInterval} from '~/utils/types/types'

    const props = defineProps<{
        ediServiceId?: number | null
        contractId: number
        catalogue: any[]
    }>()

    const valid = ref<boolean>(false)
    const isLoading = ref<boolean>(false)
    const iso2Codes = ref<string[]>([])
    const formData = ref<any>(initTransferServiceFormData())

    const emit = defineEmits<{
        (e: 'save'): void
        (e: 'cancel'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        if (props.ediServiceId) {
            await loadData()
            formData.value.ownTransferServiceDriverDescriptions.map(item => Object.assign(item, {id: nanoid()}))
        }
        if (!props.ediServiceId && props.catalogue) {
            formData.value.categoryId = props.catalogue[0].id
        }
        iso2Codes.value = Array.from(new Set(runtimeStore.countries.map(({iso2Code}) => iso2Code))).sort((a, b) =>
            a.localeCompare(b)
        )
    })

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const rq = {id: props.ediServiceId, locale: 'all'}
            const {services} = await app.$api.ownTransferServices.get(rq)
            formData.value = services[0]
        } catch (e) {
            formData.value = initTransferServiceFormData()
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            const data = clone(formData.value)
            clean(data)
            if (data.ownTransferServiceDriverDescriptions) {
                data.ownTransferServiceDriverDescriptions = data.ownTransferServiceDriverDescriptions.map(
                    ({languageCode}) => ({
                        languageCode,
                    })
                )
            }
            if (props.ediServiceId) {
                await app.$api.ownTransferServices.put({
                    contractId: props.contractId,
                    ...data,
                })
            } else {
                await app.$api.ownTransferServices.post({
                    contractId: props.contractId,
                    ...data,
                })
            }

            await supplierStore.loadProductServices()
            app.$toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const formatTime = (event, key): void => {
        const filteredValue = event.replace(/[^0-9:]/g, '')
        const formattedValue = filteredValue.replace(/(\d{2})(?=\d)/g, '$1:')
        formData.value[key] = formattedValue
    }

    const addRow = (): void => {
        const row = {languageCode: 'LOCAL', id: nanoid()}
        formData.value.ownTransferServiceDriverDescriptions.push(row)
    }

    const removeRow = (id: string): void => {
        formData.value.ownTransferServiceDriverDescriptions =
            formData.value.ownTransferServiceDriverDescriptions.filter(item => id !== item.id)
    }

    const useLanguages = computed(() => {
        const locales = Object.values(app.$conf.public.locales).map(lang => lang.toUpperCase())
        const allCodes = iso2Codes.value.filter(lang => !locales.includes(lang))
        const lang = ['LOCAL', ...locales, ...allCodes]
        return lang.map(value => ({value, title: value === 'LOCAL' ? t('languages.local') : value}))
    })

    const useLanguageCodesUnique = computed(() => {
        const seen = new Set()
        for (const item of formData.value.ownTransferServiceDriverDescriptions) {
            if (seen.has(item.languageCode)) {
                return false
            }
            seen.add(item.languageCode)
        }
        return true
    })
</script>
